<script setup lang="ts">
import { reactive } from 'vue';
import { RouterLink } from 'vue-router';
import { useItemsStore } from '../stores/items';
import ItemCard from './ItemCard.vue';
import PullToRefresh from './PullToRefresh.vue';
import { hardReset } from '../lib/hardReset';
import type { Kind } from '../types';

const items = useItemsStore();

// Pull-to-refresh — хард-ресет: чистим SW-кэши, форсим обновление воркера и
// перезагружаем страницу. Спиннер крутится, пока reload не уведёт со страницы.
function onRefresh() {
  void hardReset();
}

// Свёрнутые группы — локальное состояние. По умолчанию все развёрнуты.
const collapsed = reactive<Record<Kind, boolean>>({
  collectible: false,
  trinket: false,
  card: false,
  rune: false,
  pill: false,
} as Record<Kind, boolean>);

function toggle(k: Kind) {
  collapsed[k] = !collapsed[k];
}
</script>

<template>
  <PullToRefresh @refresh="onRefresh">
    <div v-if="!items.initialized && items.loading"
         class="flex h-full items-center justify-center text-stone-500">
      Загрузка базы…
    </div>
    <div v-else-if="items.error"
         class="flex h-full items-center justify-center px-4 text-center text-red-400">
      Ошибка: {{ items.error }}
    </div>
    <div v-else-if="items.groups.length === 0"
         class="flex h-full items-center justify-center text-stone-500">
      Ничего не найдено
    </div>

    <div v-else class="space-y-2 px-1 py-2 pb-safe">
      <section
        v-for="group in items.groups"
        :key="group.kind"
        class="rounded-md bg-bg-card/50"
        :data-testid="`group-${group.kind}`"
      >
        <button
          type="button"
          class="flex w-full items-center justify-between px-3 py-2 text-left active:bg-bg-elev"
          :aria-expanded="!collapsed[group.kind]"
          @click="toggle(group.kind)"
        >
          <span class="flex items-baseline gap-2">
            <span class="text-sm font-semibold text-stone-200">{{ group.label }}</span>
            <span class="text-xs text-stone-500">{{ group.items.length }}</span>
          </span>
          <span class="text-stone-500" aria-hidden="true">
            {{ collapsed[group.kind] ? '▸' : '▾' }}
          </span>
        </button>

        <div
          v-show="!collapsed[group.kind]"
          class="grid grid-cols-5 gap-1 px-1 pb-2"
        >
          <RouterLink
            v-for="item in group.items"
            :key="item.pk"
            :to="{ name: 'item', params: { slug: item.slug } }"
            class="contents"
          >
            <ItemCard :item="item" />
          </RouterLink>
        </div>
      </section>
    </div>
  </PullToRefresh>
</template>
